import './App.css';
import { useState, useRef, useEffect } from 'react';
import { MeetingProvider } from '@videosdk.live/react-sdk';
import { GoogleOAuthProvider } from '@react-oauth/google';
import FanimeLandingPage from './components/FanimeLandingPage';
import AvatarSelectPage from './components/AvatarSelectPage';
import ChatPage from './components/ChatPage';
import RoomManager from './components/RoomManager';
import CameraTestPage from './components/CameraTestPage';
// Firebase Analytics imports
import { analytics } from './firebase/config';
import { trackEvent } from './firebase/analytics';
import { ANALYTICS_EVENTS } from './firebase/analyticsEvents';

function App() {
  const [currentPage, setCurrentPage] = useState('landing');
  const [selectedAvatar, setSelectedAvatar] = useState(() => {
    // Try to restore selected avatar from localStorage
    const savedAvatar = localStorage.getItem('selectedAvatar');
    if (savedAvatar) {
      try {
        return JSON.parse(savedAvatar);
      } catch (error) {
        console.warn('[App] Failed to parse saved avatar:', error);
      }
    }
    return null;
  });
  const [meetingInfo, setMeetingInfo] = useState(null);
  const [meetingKey, setMeetingKey] = useState(0); // Add counter to force re-renders
  
  // Use a ref to track timer completion state
  const timerCompletionRef = useRef(null);

  // Initialize Firebase Analytics and track page views
  useEffect(() => {
    console.log('🔥 Firebase Analytics initialized:', !!analytics);
    // Track initial page load
    trackEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
      page_name: currentPage,
      timestamp: Date.now()
    });
  }, []);

  // Track page changes
  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
      page_name: currentPage,
      timestamp: Date.now()
    });
    console.log(`📊 Page view tracked: ${currentPage}`);
  }, [currentPage]);

  // Save selected avatar to localStorage whenever it changes
  useEffect(() => {
    if (selectedAvatar) {
      localStorage.setItem('selectedAvatar', JSON.stringify(selectedAvatar));
      console.log('[App] Avatar saved to localStorage:', selectedAvatar);
    }
  }, [selectedAvatar]);

  // Navigation
  const navigateTo = (page) => setCurrentPage(page);

  // Avatar selection handler
  const handleAvatarSelect = async (avatar) => {
    console.log('[App] Avatar selected:', avatar);
    setSelectedAvatar(avatar);
    // Save to localStorage for persistence
    localStorage.setItem('selectedAvatar', JSON.stringify(avatar));
    // After avatar selection, trigger matchmaking to get a room
    // This way both flows (direct and via avatar selection) use matchmaking
    try {
      const { roomAPI } = await import('./utils/apiUtils');
      const roomData = await roomAPI.matchmaking();
      handleStartChat(roomData);
    } catch (error) {
      console.error('Error during post-avatar matchmaking:', error);
      // Fallback to manual room creation if matchmaking fails
      navigateTo('room');
    }
  };

  // Room join handler (fallback for manual room creation)
  const handleRoomJoin = (info) => {
    setMeetingInfo(info);
    setMeetingKey(prev => prev + 1); // Force re-render
    navigateTo('chat');
  };

  // Handle direct chat from matchmaking
  const handleStartChat = (roomData) => {
    if (roomData) {
      // Check if user data is included in roomData (from avatar selection)
      if (roomData.user) {
        console.log('[App] User data included in roomData:', roomData.user);
        // Use the user data to set selectedAvatar
        if (roomData.user.selectedAvatar) {
          setSelectedAvatar(roomData.user.selectedAvatar);
          localStorage.setItem('selectedAvatar', JSON.stringify(roomData.user.selectedAvatar));
        } else if (roomData.user.avatarId) {
          const avatarObj = { id: roomData.user.avatarId };
          setSelectedAvatar(avatarObj);
          localStorage.setItem('selectedAvatar', JSON.stringify(avatarObj));
        }
      }
      
      // Check if we have a selected avatar (either from state, localStorage, or user data)
      const savedAvatar = localStorage.getItem('selectedAvatar');
      const savedUser = localStorage.getItem('user');
      let avatarToUse = selectedAvatar;
      
      console.log('[App] Avatar restoration debug:', {
        selectedAvatar,
        savedAvatar,
        savedUser: savedUser ? 'exists' : 'not found',
        avatarToUse
      });
      
      if (!avatarToUse && savedAvatar) {
        try {
          avatarToUse = JSON.parse(savedAvatar);
          console.log('[App] Restored avatar from selectedAvatar localStorage:', avatarToUse);
        } catch (error) {
          console.warn('[App] Failed to parse saved avatar:', error);
        }
      }
      
      // If still no avatar, check user data
      if (!avatarToUse && savedUser) {
        try {
          const user = JSON.parse(savedUser);
          console.log('[App] Parsed user data:', user);
          if (user?.selectedAvatar) {
            avatarToUse = user.selectedAvatar;
            console.log('[App] Restored avatar from user data:', avatarToUse);
          } else if (user?.avatarId) {
            // Create avatar object from avatarId
            avatarToUse = { id: user.avatarId };
            console.log('[App] Created avatar from user avatarId:', avatarToUse);
          }
        } catch (error) {
          console.warn('[App] Failed to parse user data:', error);
        }
      }
      
      // Room data from matchmaking API - navigate directly to chat
      console.log('Starting chat with room data:', roomData);
      console.log('Using avatar:', avatarToUse);
      
      const videoToken = roomData.videoToken;
      console.log('VideoSDK token being used:', videoToken);
      console.log('VideoSDK token length:', videoToken?.length);
      console.log('Environment token:', process.env.REACT_APP_VIDEO_TOKEN);
      
      setMeetingInfo({
        roomId: roomData.roomId,
        token: videoToken
      });
      setMeetingKey(prev => prev + 1); // Force re-render
      navigateTo('chat');
    } else {
      // No room data - go through avatar selection flow
      navigateTo('select-avatar');
    }
  };

  const handleMeetingLeft = async (options) => {
    console.log('🔍 handleMeetingLeft called with options:', options);
    console.log('🔍 Type of options:', typeof options);
    console.log('🔍 Options.timerComplete:', options?.timerComplete);
    console.log('🔍 Options.stayOnPage:', options?.stayOnPage);
    
    // If options are provided (from ChatPage), store them in the ref
    if (options) {
      timerCompletionRef.current = options;
      console.log('💾 Stored timer completion options in ref:', options);
    }
    
    // Check if this is a timer completion (either from options or stored in ref)
    const timerOptions = options || timerCompletionRef.current;
    console.log('🔍 Final timerOptions being checked:', timerOptions);
    
    // Check if this is a home button click (should go to landing page)
    if (timerOptions?.goToLanding) {
      console.log('🏠 Home button clicked, going to landing page');
      timerCompletionRef.current = null;
      setMeetingInfo(null);
      setSelectedAvatar(null);
      setMeetingKey(0); // Reset meeting key
      navigateTo('landing');
      return;
    }
    
    if (timerOptions?.stayOnPage && timerOptions?.timerComplete) {
      console.log('🔄 Timer completion detected, starting automatic re-matchmaking...');
      
      // This is triggered when the timer completes.
      // We want to find a new room and stay on the ChatPage.
      try {
        // First, leave the current room on the backend (for skip/timer completion)
        if (meetingInfo?.roomId) {
          console.log('🚪 Leaving current room on backend:', meetingInfo.roomId);
          try {
            const { roomAPI } = await import('./utils/apiUtils');
            await roomAPI.leaveRoom(meetingInfo.roomId, 'timer-complete');
            console.log('✅ Successfully left room on backend');
          } catch (leaveError) {
            console.warn('⚠️ Error leaving room on backend (continuing anyway):', leaveError);
          }
        }
        
        console.log('📞 Importing roomAPI...');
        const { roomAPI } = await import('./utils/apiUtils');
        console.log('🎯 Calling matchmaking API...');
        const roomData = await roomAPI.matchmaking();
        console.log('✅ Got new room data:', roomData);
        
        // By updating the meetingInfo, the MeetingProvider will re-render
        // with the new room details, effectively joining a new meeting.
        const newMeetingInfo = {
          roomId: roomData.roomId,
          token: roomData.videoToken,
        };
        console.log('🏠 Setting new meeting info:', newMeetingInfo);
        console.log('🔄 Previous meeting info was:', meetingInfo);
        setMeetingInfo(newMeetingInfo);
        setMeetingKey(prev => prev + 1); // Force re-render even if room ID is the same
        console.log('✨ Updated meeting info, staying on chat page');
        console.log('🔑 Meeting key incremented to force re-render');
        
        // Clear the ref after a small delay to ensure MeetingProvider doesn't interfere
        setTimeout(() => {
          timerCompletionRef.current = null;
          console.log('🧹 Cleared timer completion ref after delay');
        }, 1000);
        
        // The user stays on the 'chat' page.
      } catch (error) {
        console.error('❌ Error during automatic re-matchmaking:', error);
        console.error('❌ Error details:', error.message);
        console.error('❌ Error stack:', error.stack);
        // If finding a new room fails, fall back to the landing page.
        timerCompletionRef.current = null;
        setMeetingInfo(null);
        setSelectedAvatar(null);
        setMeetingKey(0); // Reset meeting key
        navigateTo('landing');
      }
    } else {
      console.log('🏠 Normal meeting left, going to landing page');
      console.log('🏠 Reason: stayOnPage =', timerOptions?.stayOnPage, ', timerComplete =', timerOptions?.timerComplete);
      // Default behavior: go back to the landing page.
      // This handles cases like the user manually leaving the room.
      timerCompletionRef.current = null;
      setMeetingInfo(null);
      setSelectedAvatar(null);
      setMeetingKey(0); // Reset meeting key
      navigateTo('landing');
    }
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <div className="App">
        {currentPage === 'landing' && (
          <FanimeLandingPage 
            onStartChatting={handleStartChat} 
          />
        )}
        {currentPage === 'select-avatar' && (
          <AvatarSelectPage
            onAvatarSelect={handleAvatarSelect}
            onBack={() => navigateTo('landing')}
          />
        )}
        {currentPage === 'room' && (
          <RoomManager onJoin={handleRoomJoin} />
        )}
        {currentPage === 'chat' && meetingInfo && (
          <MeetingProvider
            key={`${meetingInfo.roomId}-${meetingKey}`} // Use both room ID and counter for unique key
            config={{
              meetingId: meetingInfo.roomId,
              // Debug logging
              ...(() => {
                console.log('[App] MeetingProvider config:', {
                  selectedAvatar,
                  avatarId: selectedAvatar?.id || 'm1',
                  meetingInfo
                });
                return {};
              })(),
              micEnabled: true,
              webcamEnabled: true,
              name: (() => {
                const savedUser = localStorage.getItem('user');
                try {
                  const user = savedUser ? JSON.parse(savedUser) : null;
                  return user?.username || user?.name || 'User';
                } catch {
                  return 'User';
                }
              })(),
              // Include avatar ID in participant metadata
              metaData: {
                avatarId: (() => {
                  // Use selectedAvatar from state, or try to restore from localStorage/user data
                  if (selectedAvatar?.id) {
                    return selectedAvatar.id;
                  }
                  
                  // Check selectedAvatar localStorage
                  const savedAvatar = localStorage.getItem('selectedAvatar');
                  if (savedAvatar) {
                    try {
                      const parsed = JSON.parse(savedAvatar);
                      return parsed.id || 'm1';
                    } catch (error) {
                      console.warn('[App] Failed to parse saved avatar for metadata:', error);
                    }
                  }
                  
                  // Check user data
                  const savedUser = localStorage.getItem('user');
                  if (savedUser) {
                    try {
                      const user = JSON.parse(savedUser);
                      if (user?.selectedAvatar?.id) {
                        return user.selectedAvatar.id;
                      }
                      if (user?.avatarId) {
                        return user.avatarId;
                      }
                    } catch (error) {
                      console.warn('[App] Failed to parse user data for metadata:', error);
                    }
                  }
                  
                  return 'm1';
                })()
              },
              // Advanced audio configuration to prevent echo and feedback
              multiStream: true,
              audioCaptureConfig: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                sampleRate: 48000,
                channelCount: 1
              },
              // Ensure proper audio codec selection
              preferredCodec: 'opus'
            }}
            token={meetingInfo.token}
            joinWithoutUserInteraction
            onMeetingJoined={() => {
              console.log('=== Meeting joined successfully ===');
              console.log('Meeting ID:', meetingInfo.roomId);
              console.log('Token length:', meetingInfo.token?.length);
              console.log('Token preview:', meetingInfo.token?.substring(0, 50) + '...');
              console.log('🕐 Timestamp:', new Date().toISOString());
              console.log('🆔 Meeting Provider Key:', meetingInfo.roomId);
              console.log('Selected Avatar:', selectedAvatar);
              console.log('Avatar ID in metadata:', selectedAvatar?.id || 'm1');
              console.log('================================');
            }}
            onMeetingLeft={() => {
              console.log('🔍 MeetingProvider onMeetingLeft triggered');
              console.log('🔍 Current timerCompletionRef:', timerCompletionRef.current);
              // Only handle this if there's no timer completion in progress
              // If timerCompletionRef has data, it means we're in the middle of a skip/timer complete
              if (!timerCompletionRef.current) {
                console.log('📤 No timer completion in progress, handling normal meeting left');
                handleMeetingLeft();
              } else {
                console.log('⏳ Timer completion in progress, letting ChatPage handle it');
              }
            }}
            onError={(error) => {
              console.error('VideoSDK MeetingProvider error:', error);
              // Show a user-friendly error message
              alert(`Unable to join the video call. Please try again. Error: ${error.message || 'Unknown error'}`);
              // Navigate back to landing page
              navigateTo('landing');
            }}
          >
              <ChatPage
                selectedAvatar={selectedAvatar}
                onBack={handleMeetingLeft}
                meetingId={meetingInfo.roomId}
                authToken={meetingInfo.token}
              />
            </MeetingProvider>
        )}
        {currentPage === 'camera-test' && (
          <CameraTestPage onBack={() => navigateTo('landing')} />
        )}
      </div>
    </GoogleOAuthProvider>
  );
}

export default App;
